/**
 * @license
 * Copyright Akveo. All Rights Reserved.
 * Licensed under the MIT License. See License.txt in the project root for license information.
 */
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgModule } from '@angular/core';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { CoreModule } from './@core/core.module';
import { ThemeModule } from './@theme/theme.module';
import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';
import {
  NbAlertModule,
  NbButtonModule,
  NbChatModule,
  NbCheckboxModule,
  NbDatepickerModule,
  NbDialogModule,
  NbFormFieldModule,
  NbIconModule,
  NbInputModule,
  NbMenuModule,
  NbSidebarModule,
  NbToastrModule,
  NbWindowModule,
} from '@nebular/theme';
import { NgxLoginComponent } from './@theme/components/auth/login/login.component';
import { NgxRequestPasswordComponent } from './@theme/components/auth/request-password/request-password.component';
import { NgxResetPasswordComponent } from './@theme/components/auth/reset-password/reset-password.component';
import { NgxVerifyCodeComponent } from './@theme/components/auth/verify-code/verify-code.component';
import { NgxNewPasswordComponent } from './@theme/components/auth/new-password/new-password.component';
import { getDeepFromObject, NbAuthJWTToken, NbAuthModule, NbPasswordAuthStrategy } from '@nebular/auth';
import { environment } from '../environments/environment';
import { FormsModule } from '@angular/forms';
import { NgxLogoutComponent } from './@theme/components/auth/logout/logout/logout.component';
import { AuthGuard } from './auth-guard.service';
import { CookieBannerComponent } from './shared/components/cookie-banner/cookie-banner.component';
import { CookieInterceptor } from './shared/interceptors/cookie-interceptor';
import { PoliticaCookiesModalComponent } from './shared/components/politica-cookies-modal/politica-cookies-modal.component';
import { RetryInterceptor } from './shared/interceptors/retry-interceptor';
import { ClarityService } from './@core/utils/clarity.service';

console.log('Environment API URL:', environment.apiUrl);

const API_URL = environment.apiUrl;

const COMPONENTS = [
  AppComponent,
  NgxLoginComponent,
  NgxRequestPasswordComponent,
  NgxVerifyCodeComponent,
  NgxNewPasswordComponent,
  NgxResetPasswordComponent,
  NgxLogoutComponent,
  CookieBannerComponent,
  PoliticaCookiesModalComponent
]
const formSetting: any = {
  redirectDelay: 0,
  showMessages: {
    success: true,
  },
};

@NgModule({
  providers: [
    AuthGuard,
    ClarityService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: CookieInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: RetryInterceptor,
      multi: true
    }
  ],
  declarations: [COMPONENTS],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    HttpClientModule,
    AppRoutingModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatAutocompleteModule,
    MatInputModule,
    MatFormFieldModule,
    MatIconModule,
    NbAlertModule,
    NbButtonModule,
    NbInputModule,
    NbFormFieldModule,
    NbIconModule,
    NbCheckboxModule,
    NbSidebarModule.forRoot(),
    NbMenuModule.forRoot(),
    NbDatepickerModule.forRoot(),
    NbDialogModule.forRoot(),
    NbWindowModule.forRoot(),
    NbToastrModule.forRoot(),
    NbChatModule.forRoot({
      messageGoogleMapKey: 'AIzaSyA_wNuCzia92MAmdLRzmqitRGvCF7wCZPY',
    }),
    CoreModule.forRoot(),
    ThemeModule.forRoot(),
    NbAuthModule.forRoot({
      strategies: [
        NbPasswordAuthStrategy.setup({
          name: 'email',
          baseEndpoint: environment.apiUrl,
          login: {
            endpoint: 'v1/Account/sign-in',
            method: 'post',
            redirect: {
              success: '/pages', // Mudando para /pages em vez de /pages/dashboard
              failure: null,
            },
          },
          register: {
            endpoint: 'v1/Account/sign-up',
            method: 'post',
            redirect: {
              success: '/auth/login',
              failure: null,
            },
          },
          requestPass: {
            endpoint: 'v1/Account/request-password',
            method: 'post',
            redirect: {
              success: null, // Não redirecionar automaticamente
              failure: null,
            },
          },
          logout: {
            endpoint: null, // Desabilitar endpoint de logout
            method: 'post',
            redirect: {
              success: '/auth/login', // Sempre redirecionar para login
              failure: '/auth/login', // Em caso de falha também
            },
          },
          refreshToken: {
            endpoint: 'v1/Account/refresh-token',
            method: 'post',
            redirect: {
              success: null,
              failure: null,
            },
          },
          token: {
            class: NbAuthJWTToken,
            key: 'token',
            // API pode retornar "Bearer <jwt>" e JSON com token ou Token (PropertyNamingPolicy = null no ASP.NET)
            getter: (module: string, res: any, options: any) => {
              const pathKey = options?.token?.key ?? 'token';
              const body = res?.body;
              if (body == null || typeof body !== 'object') {
                return undefined;
              }
              let raw = getDeepFromObject(body, pathKey) as string | undefined | null;
              if (raw == null || raw === '') {
                raw = (body as Record<string, string>).token ?? (body as Record<string, string>).Token;
              }
              if (typeof raw !== 'string') {
                return undefined;
              }
              const jwt = raw.replace(/^Bearer\s+/i, '').trim();
              return jwt || undefined;
            },
          },
          errors: {
            getter: (module: string, res: any) => {
              // HttpClient em erro HTTP coloca o JSON em `error`; em sucesso estaria em `body`
              const responseBody = res?.error ?? res?.body ?? res;
              if (!responseBody) {
                return ['Erro desconhecido'];
              }
              // API v1/Account/sign-in: { success: false, errors: [{ Property, Message }] }
              const apiErrors = responseBody.errors;
              if (Array.isArray(apiErrors) && apiErrors.length > 0) {
                const msgs = apiErrors
                  .map((e: { Message?: string; message?: string }) => e?.Message ?? e?.message ?? '')
                  .filter((m: string) => !!m);
                if (msgs.length > 0) {
                  return msgs;
                }
              }
              const nested =
                responseBody.error?.message ??
                responseBody.error?.Message ??
                responseBody.Message ??
                responseBody.message;
              if (nested) {
                return [nested];
              }
              return ['Erro desconhecido'];
            },
          },
          messages: {
            getter: (module: string, res: any) => {
              const responseBody = res?.error ?? res?.body ?? res;
              return responseBody?.message || 'Operação realizada com sucesso';
            },
          },
        }),
      ],

      // providers: {
      //   email: {
      //     // service: NbEmailPassAuthProvider,
      //     config: {
      //       baseEndpoint: API_URL,
      //       login: {
      //         endpoint: '/auth/sign-in',
      //         method: 'post',
      //       },
      //       logout: {
      //         endpoint: '/auth/sign-out',
      //         method: 'post',
      //       },
      //       token: {
      //         key: 'token',
      //       },
      //     },
      //   },
      //},
      forms: {
          login: formSetting,
        // register: formSetting,
        requestPassword: formSetting,
        // resetPassword: formSetting,
        logout: {
          redirectDelay: 0,
          strategy: 'email',
        },
      },

    }),
    
  ],
  bootstrap: [AppComponent],
})
export class AppModule {
}
